import type {
  FinanceStateDefinition,
  FinanceStakeTargetId,
  ModuleDefinition,
} from "./phase-three-types";
import { SeededRandom, hashSeed } from "./rng";

export const LUCKY_STAR_HIDDEN_EFFECT_ID = "lucky_star";

export function resolvePeriodicSpendAccumulatorCycle(
  runSeed: string,
  playerId: string,
  slot: number,
  ordinal: number,
  definition: NonNullable<
    FinanceStateDefinition["periodicSpendAccumulator"]
  >,
): { rewardAmount: number; rareSuccess: boolean } {
  const reward = definition.thresholdReward;
  const rewardRoll = new SeededRandom(
    hashSeed(
      `${runSeed}|finance-threshold-reward|${playerId}|${slot}|${ordinal}`,
    ),
  ).next();
  const rewardAmount =
    reward.minInclusive +
    Math.floor(rewardRoll * (reward.maxInclusive - reward.minInclusive + 1));
  const rareRoll = new SeededRandom(
    hashSeed(
      `${runSeed}|finance-rare-transition|${playerId}|${slot}|${ordinal}`,
    ),
  ).next();
  return {
    rewardAmount,
    rareSuccess: rareRoll * 100 < definition.rareTransition.successPercent,
  };
}

export interface FinanceStakeTargetDefinition {
  id: FinanceStakeTargetId;
  name: string;
  kind: "vital" | "attribute" | "resource";
}

/**
 * The authoritative stake pool is intentionally explicit. Every entry maps to
 * either a mutable player value or the persistent finance adjustment ledger;
 * no read-only derived stat is ever written directly.
 */
export const FINANCE_STAKE_TARGETS: readonly FinanceStakeTargetDefinition[] = [
  { id: "current_health", name: "当前生命", kind: "vital" },
  { id: "max_health", name: "最大生命", kind: "attribute" },
  { id: "health_regen", name: "生命恢复", kind: "attribute" },
  { id: "health_regen_ratio", name: "最大生命比例恢复", kind: "attribute" },
  { id: "current_mana", name: "当前魔力", kind: "vital" },
  { id: "max_mana", name: "最大魔力", kind: "attribute" },
  { id: "mana_regen", name: "魔力恢复", kind: "attribute" },
  { id: "mana_regen_ratio", name: "最大魔力比例恢复", kind: "attribute" },
  { id: "attack_damage", name: "攻击力", kind: "attribute" },
  { id: "damage_multiplier", name: "总伤害倍率", kind: "attribute" },
  { id: "attack_damage_multiplier", name: "普通攻击伤害倍率", kind: "attribute" },
  { id: "ability_damage_multiplier", name: "技能伤害倍率", kind: "attribute" },
  { id: "base_attack_speed", name: "基础攻击速度", kind: "attribute" },
  { id: "bonus_attack_speed", name: "加成攻击速度", kind: "attribute" },
  { id: "attack_range", name: "攻击射程", kind: "attribute" },
  { id: "base_move_speed", name: "基础移动速度", kind: "attribute" },
  { id: "final_move_speed", name: "最终移动速度", kind: "attribute" },
  { id: "move_speed_multiplier", name: "移动速度倍率", kind: "attribute" },
  { id: "armor", name: "护甲", kind: "attribute" },
  { id: "dodge", name: "闪避率", kind: "attribute" },
  { id: "repair_per_second", name: "每秒修理", kind: "attribute" },
  { id: "healing_per_second", name: "每秒治疗", kind: "attribute" },
  { id: "scouting_radius", name: "侦查范围", kind: "attribute" },
  { id: "reveal_radius", name: "反隐范围", kind: "attribute" },
  { id: "splash_radius", name: "普攻溅射范围", kind: "attribute" },
  { id: "chain_targets", name: "连锁目标数", kind: "attribute" },
  { id: "pierce_targets", name: "穿透目标数", kind: "attribute" },
  { id: "crystal_drop_chance", name: "结晶掉落率", kind: "attribute" },
  { id: "elite_damage_multiplier", name: "精英伤害倍率", kind: "attribute" },
  { id: "companion_damage_multiplier", name: "伙伴伤害倍率", kind: "attribute" },
  { id: "summon_cap", name: "召唤上限", kind: "attribute" },
  { id: "gold_multiplier", name: "金币获取倍率", kind: "attribute" },
  { id: "experience_multiplier", name: "经验获取倍率", kind: "attribute" },
  { id: "surface_mining_extra_gold_flat", name: "采矿每次额外金币", kind: "attribute" },
  { id: "surface_mining_extra_gold_multiplier", name: "采矿额外金币倍率", kind: "attribute" },
  { id: "surface_mining_speed_bonus", name: "采矿速度加成", kind: "attribute" },
  { id: "surface_mining_gold_yield_bonus", name: "采矿金币收益加成", kind: "attribute" },
  { id: "surface_mining_crystal_yield_ratio", name: "采矿结晶收益比例", kind: "attribute" },
  { id: "gold", name: "金币", kind: "resource" },
  { id: "crystal", name: "结晶", kind: "resource" },
  { id: "experience", name: "当前等级经验", kind: "resource" },
  { id: "talent_points", name: "未分配本源点", kind: "resource" },
  { id: "wage", name: "每分钟工资", kind: "resource" },
] as const;

export const FINANCE_STAKE_TARGET_IDS = FINANCE_STAKE_TARGETS.map(
  (target) => target.id,
);

const FINANCE_STAKE_TARGET_BY_ID = new Map(
  FINANCE_STAKE_TARGETS.map((target) => [target.id, target]),
);

export function getFinanceStakeTarget(
  id: FinanceStakeTargetId,
): FinanceStakeTargetDefinition {
  const target = FINANCE_STAKE_TARGET_BY_ID.get(id);
  if (!target) {
    throw new Error(`未知理财赌注项目：${id}`);
  }
  return target;
}

export function getFinanceStateTargetIds(
  module: ModuleDefinition,
): string[] {
  const finance = module.financeState;
  if (!finance) {
    return module.stateFamily?.canonicalModuleId
      ? [module.stateFamily.canonicalModuleId]
      : [];
  }
  const timedTargets =
    finance.timedOutcome?.kind === "weighted_branches"
      ? finance.timedOutcome.branches.map((branch) => branch.targetModuleId)
      : finance.timedOutcome?.kind === "stake_resolution"
        ? [finance.timedOutcome.returnTargetModuleId]
        : [];
  return [
    finance.resourceGate?.targetModuleId,
    finance.timedTransition?.targetModuleId,
    finance.randomStake?.targetModuleId,
    finance.periodicSpendAccumulator?.rareTransition.targetModuleId,
    finance.periodicCharge?.targetModuleId,
    ...timedTargets,
    module.stateFamily?.canonicalModuleId,
  ].filter((id): id is string => Boolean(id));
}

function finitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function finitePercent(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 100;
}

/**
 * Validates one finance-state node against the complete active module graph.
 * Timed loops are legal; only an all-immediate random-stake cycle is rejected.
 */
export function validateFinanceModuleDefinition(
  module: ModuleDefinition,
  modules: ReadonlyMap<string, ModuleDefinition>,
  hiddenEffectIds?: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];
  const finance = module.financeState;
  const family = module.stateFamily;
  if (!finance) {
    if (family) {
      errors.push("stateFamily requires financeState");
    }
    return errors;
  }
  if (module.category !== "finance") {
    errors.push("financeState is only valid for finance modules");
  }
  if (typeof finance.familyId !== "string" || !finance.familyId.trim()) {
    errors.push("financeState.familyId must not be empty");
    return errors;
  }
  const configuredActions = [
    finance.timeScaledIncome,
    finance.resourceGate,
    finance.timedOutcome,
    finance.onEnterGrant,
    finance.timedTransition,
    finance.periodicDrain,
    finance.periodicSpendAccumulator,
    finance.periodicCharge,
    finance.randomStake,
  ].filter(Boolean).length;
  const grantWithTransition = Boolean(
    finance.onEnterGrant && finance.timedTransition,
  );
  if (configuredActions > (grantWithTransition ? 2 : 1)) {
    errors.push(
      "a finance state may define one action, except onEnterGrant plus timedTransition",
    );
  }
  const validateTarget = (targetId: string, label: string): void => {
    if (typeof targetId !== "string" || !targetId) {
      errors.push(`${label} must reference a module`);
      return;
    }
    const target = modules.get(targetId);
    if (!target) {
      errors.push(`${label} references missing module ${targetId}`);
      return;
    }
    if (
      target.tier !== module.tier ||
      target.financeState?.familyId !== finance.familyId
    ) {
      errors.push(`${label} must target the same-tier finance family`);
    }
  };
  const validateHiddenEffect = (id: string, label: string): void => {
    if (
      typeof id !== "string" ||
      !id.trim() ||
      (hiddenEffectIds && !hiddenEffectIds.has(id))
    ) {
      errors.push(`${label} references unknown repeatable hidden effect ${id}`);
    }
  };
  if (finance.timeScaledIncome) {
    const income = finance.timeScaledIncome;
    if (
      !finitePositive(income.intervalMs) ||
      !finitePositive(income.elapsedUnitMs) ||
      income.round !== "ceil"
    ) {
      errors.push("timeScaledIncome interval/unit/round is invalid");
    }
  }
  if (finance.resourceGate) {
    if (!finitePositive(finance.resourceGate.cost)) {
      errors.push("resourceGate cost must be positive");
    }
    validateTarget(finance.resourceGate.targetModuleId, "resourceGate");
  }
  if (finance.timedOutcome) {
    const outcome = finance.timedOutcome;
    if (!finitePositive(outcome.delayMs)) {
      errors.push("timedOutcome delay must be positive");
    }
    if (outcome.kind === "weighted_branches") {
      if (!Array.isArray(outcome.branches)) {
        errors.push("weighted finance outcome branches must be an array");
        return errors;
      }
      const total = outcome.branches.reduce(
        (sum, branch) => sum + branch.weight,
        0,
      );
      if (
        outcome.branches.length < 2 ||
        new Set(outcome.branches.map((branch) => branch.targetModuleId))
          .size !== outcome.branches.length ||
        outcome.branches.some(
          (branch) =>
            !finitePositive(branch.weight) ||
            (branch.reward !== undefined &&
              (!finitePositive(branch.reward.amount) ||
                !["gold", "crystal"].includes(branch.reward.resource))),
        ) ||
        Math.abs(total - 100) > 1e-9
      ) {
        errors.push("weighted finance outcome must contain unique positive branches totaling 100");
      }
      for (const branch of outcome.branches) {
        validateTarget(branch.targetModuleId, "timedOutcome branch");
      }
    } else {
      const hasFixed = outcome.fixedSuccessPercent !== undefined;
      const hasDynamic = outcome.dynamicSuccess !== undefined;
      if (hasFixed === hasDynamic) {
        errors.push("stake outcome must define exactly one fixed or dynamic probability");
      }
      if (hasFixed && !finitePercent(outcome.fixedSuccessPercent!)) {
        errors.push("fixed stake success percent must be between 0 and 100");
      }
      if (outcome.dynamicSuccess) {
        const dynamic = outcome.dynamicSuccess;
        if (
          !finitePercent(dynamic.basePercent) ||
          !finitePositive(dynamic.percentPerCount) ||
          !finitePercent(dynamic.maxPercent) ||
          dynamic.maxPercent < dynamic.basePercent
        ) {
          errors.push("dynamic stake probability is invalid");
        }
        validateHiddenEffect(
          dynamic.repeatableHiddenEffectId,
          "dynamicSuccess",
        );
      }
      if (!finitePositive(outcome.stakeReturnMultiplier)) {
        errors.push("stake return multiplier must be positive");
      }
      if (outcome.successGrant) {
        if (
          !Number.isInteger(outcome.successGrant.count) ||
          outcome.successGrant.count <= 0
        ) {
          errors.push("stake success hidden-effect grant count must be positive integer");
        }
        validateHiddenEffect(
          outcome.successGrant.repeatableHiddenEffectId,
          "stake success grant",
        );
      }
      validateTarget(outcome.returnTargetModuleId, "stake outcome return");
    }
  }
  if (finance.onEnterGrant) {
    const grant = finance.onEnterGrant;
    const hasHiddenGrant =
      grant.repeatableHiddenEffectId !== undefined || grant.count !== undefined;
    const hasResourceGrants = Boolean(grant.resources?.length);
    if (!hasHiddenGrant && !hasResourceGrants) {
      errors.push("onEnterGrant must define a resource or hidden-effect grant");
    }
    if (hasHiddenGrant) {
      if (
        !grant.repeatableHiddenEffectId ||
        !Number.isInteger(grant.count) ||
        (grant.count ?? 0) <= 0
      ) {
        errors.push("on-enter hidden-effect grant id/count must be complete and positive");
      } else {
        validateHiddenEffect(
          grant.repeatableHiddenEffectId,
          "onEnterGrant",
        );
      }
    }
    if (
      grant.resources?.some(
        (resourceGrant) =>
          !["gold", "crystal"].includes(resourceGrant.resource) ||
          !finitePositive(resourceGrant.amount),
      )
    ) {
      errors.push("on-enter resource grants must use positive writable resources");
    }
  }
  if (finance.timedTransition) {
    if (!finitePositive(finance.timedTransition.delayMs)) {
      errors.push("timedTransition delay must be positive");
    }
    validateTarget(
      finance.timedTransition.targetModuleId,
      "timedTransition",
    );
  }
  if (finance.periodicDrain) {
    if (
      !finitePositive(finance.periodicDrain.intervalMs) ||
      !finitePositive(finance.periodicDrain.amount) ||
      !Number.isFinite(finance.periodicDrain.floor) ||
      finance.periodicDrain.floor < 0
    ) {
      errors.push("periodicDrain interval/amount/floor is invalid");
    }
  }
  if (finance.periodicSpendAccumulator) {
    const accumulator = finance.periodicSpendAccumulator;
    const reward = accumulator.thresholdReward;
    if (
      !finitePositive(accumulator.intervalMs) ||
      !finitePositive(accumulator.costPerTick) ||
      !finitePositive(accumulator.threshold) ||
      accumulator.threshold < accumulator.costPerTick ||
      !Number.isInteger(reward.minInclusive) ||
      !Number.isInteger(reward.maxInclusive) ||
      reward.minInclusive < 0 ||
      reward.maxInclusive < reward.minInclusive ||
      reward.integer !== true ||
      !finitePercent(accumulator.rareTransition.successPercent) ||
      accumulator.resetOnFailure !== true
    ) {
      errors.push("periodicSpendAccumulator interval/cost/threshold/reward/probability is invalid");
    }
    validateTarget(
      accumulator.rareTransition.targetModuleId,
      "periodicSpendAccumulator rare transition",
    );
  }
  if (finance.periodicCharge) {
    const charge = finance.periodicCharge;
    if (
      !finitePositive(charge.intervalMs) ||
      !finitePositive(charge.amountPerTick) ||
      !finitePositive(charge.threshold) ||
      charge.amountPerTick > charge.threshold
    ) {
      errors.push("periodicCharge interval/amount/threshold is invalid");
    }
    validateTarget(charge.targetModuleId, "periodicCharge");
  }
  if (
    finance.onReplacementReward &&
    (!["gold", "crystal"].includes(finance.onReplacementReward.resource) ||
      !finitePositive(finance.onReplacementReward.amount))
  ) {
    errors.push("onReplacementReward must use a positive writable resource");
  }
  if (finance.randomStake) {
    const stake = finance.randomStake;
    if (!Array.isArray(stake.targetPool)) {
      errors.push("randomStake targetPool must be an array");
      return errors;
    }
    const pool = new Set(stake.targetPool);
    if (
      !finitePositive(stake.ratio) ||
      stake.ratio > 1 ||
      stake.targetPool.length === 0 ||
      pool.size !== stake.targetPool.length ||
      stake.targetPool.some(
        (targetId) => !FINANCE_STAKE_TARGET_BY_ID.has(targetId),
      )
    ) {
      errors.push("randomStake ratio or writable target pool is invalid");
    }
    validateTarget(stake.targetModuleId, "randomStake");
    if (!modules.get(stake.targetModuleId)?.financeState?.timedOutcome) {
      errors.push("randomStake must transition to a timed outcome state");
    }
  }
  if (family) {
    const canonical = modules.get(family.canonicalModuleId);
    if (
      typeof family.familyId !== "string" ||
      !family.familyId.trim() ||
      typeof family.canonicalModuleId !== "string" ||
      family.familyId !== finance.familyId ||
      !canonical ||
      canonical.tier !== module.tier ||
      canonical.financeState?.familyId !== finance.familyId ||
      family.inheritsAscension === family.inheritsSlotLock
    ) {
      errors.push("stateFamily canonical/inheritance configuration is invalid");
    } else if (
      family.inheritsAscension &&
      (module.tier !== 5 || !canonical.evolutionTo)
    ) {
      errors.push("ascension proxy must reference an ascendable tier-5 canonical state");
    } else if (
      family.inheritsSlotLock &&
      (module.tier !== 6 || !canonical.evolutionFrom)
    ) {
      errors.push("slot-lock proxy must reference an ascended tier-6 canonical state");
    }
  }
  return errors;
}

export function validateFinanceImmediateTransitionCycles(
  modules: readonly ModuleDefinition[],
): string[] {
  const immediate = new Map<string, string>();
  for (const module of modules) {
    if (module.financeState?.randomStake) {
      immediate.set(module.id, module.financeState.randomStake.targetModuleId);
    }
  }
  const errors: string[] = [];
  for (const start of immediate.keys()) {
    const seen = new Set<string>();
    let current: string | undefined = start;
    while (current && immediate.has(current)) {
      if (seen.has(current)) {
        errors.push(`finance state ${start} has an immediate transition cycle`);
        break;
      }
      seen.add(current);
      current = immediate.get(current);
    }
  }
  return [...new Set(errors)];
}
